import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePlanPricing } from '@/hooks/usePlanPricing';
import { calculateProration as calcProrationPure } from '@/lib/proration';

export type PlanType = 'free' | 'pro' | 'business';
export type BillingPeriod = 'monthly' | 'annual';

export type FeatureKey =
  | 'questions_per_day'
  | 'simulations_per_month'
  | 'flashcards_per_day'
  | 'study_groups'
  | 'ai_assistant'
  | 'study_plan'
  | 'advanced_reports'
  | 'video_lessons'
  | 'seats';

interface PlanLimit {
  feature: string;
  limit_value: number;
}

export const PLAN_PRICES: Record<Exclude<PlanType, 'free'>, Record<BillingPeriod, number>> = {
  pro: { monthly: 29.90, annual: 299.00 },
  business: { monthly: 99.90, annual: 999.00 },
};

interface SubscriptionData {
  plan: PlanType;
  billingPeriod: BillingPeriod;
  expiresAt: string | null;
  startedAt: string | null;
  amountPaid: number;
  limits: Record<string, number>;
  isPro: boolean;
  isBusiness: boolean;
  isMonthly: boolean;
  isAnnual: boolean;
  isFree: boolean;
  canAccess: (feature: FeatureKey) => boolean;
  getLimit: (feature: FeatureKey) => number;
  isUnlimited: (feature: FeatureKey) => boolean;
  calculateProration: (newPlan: PlanType, newPeriod: BillingPeriod) => { credit: number; amountDue: number; finalAmount: number };
  loading: boolean;
}

export function useSubscription(): SubscriptionData {
  const { user } = useAuth();

  const { data: subscription, isLoading: planLoading } = useQuery({
    queryKey: ['user-subscription', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from('user_subscriptions')
        .select('plan, status, expires_at, started_at, billing_period, amount_paid')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (!data) return null;
      if (data.expires_at && new Date(data.expires_at) < new Date()) return null;
      return data;
    },
    enabled: !!user,
    staleTime: 60000,
  });

  const currentPlan = (subscription?.plan as PlanType) || 'free';
  const billingPeriod = (subscription?.billing_period as BillingPeriod) || 'monthly';
  const expiresAt = subscription?.expires_at || null;
  const startedAt = subscription?.started_at || null;
  const amountPaid = Number(subscription?.amount_paid) || 0;

  const { data: limits, isLoading: limitsLoading } = useQuery({
    queryKey: ['plan-limits', currentPlan],
    queryFn: async () => {
      const { data } = await supabase
        .from('plan_limits')
        .select('feature, limit_value')
        .eq('plan', currentPlan);

      const map: Record<string, number> = {};
      (data as PlanLimit[] | null)?.forEach((l) => {
        map[l.feature] = l.limit_value;
      });
      return map;
    },
    enabled: !!user,
    staleTime: 300000,
  });

  const currentLimits = limits || {};

  const canAccess = (feature: FeatureKey): boolean => {
    const val = currentLimits[feature];
    if (val === undefined) return true;
    return val !== 0;
  };

  const getLimit = (feature: FeatureKey): number => {
    return currentLimits[feature] ?? -1;
  };

  const isUnlimited = (feature: FeatureKey): boolean => {
    return (currentLimits[feature] ?? -1) === -1;
  };

  const { priceFor } = usePlanPricing();

  const calculateProration = (newPlan: PlanType, newPeriod: BillingPeriod) =>
    calcProrationPure({
      currentPlan,
      billingPeriod,
      expiresAt,
      startedAt,
      amountPaid,
      newPlan,
      newPeriod,
      newPrice: priceFor(newPeriod),
    });

  return {
    plan: currentPlan,
    billingPeriod,
    expiresAt,
    startedAt,
    amountPaid,
    limits: currentLimits,
    isPro: currentPlan === 'pro',
    isBusiness: currentPlan === 'business',
    isMonthly: currentPlan === 'pro',
    isAnnual: currentPlan === 'business',
    isFree: currentPlan === 'free',
    canAccess,
    getLimit,
    isUnlimited,
    calculateProration,
    loading: planLoading || limitsLoading,
  };
}
